import { useEffect, useMemo, useState } from 'react';

import { decodeDesign, DesignDecodeError } from '../engine/serialize';
import { buildGuide, guideToText, formatMinutes } from '../engine/guide';
import { fmt, round1, describeCut, JOIN_ALLOWANCE_CM } from '../engine/thread';
import { findRepeatLength } from '../engine/normal';
import { KNOT_BEHAVIOUR, type KnotType } from '../engine/knots';
import { planModule } from '../engine/chain';
import { BraceletPreview, PatternChart, PaletteStrip } from './Preview';
import { hrefFor } from './router';
import { designId, loadProgress, rememberDesign } from '../state/storage';
import type { Settings } from '../state/storage';
import type { Design } from '../engine/types';

interface Props {
  code: string;
  settings: Settings;
  /** Print view drops the interactive chrome and expands everything. */
  printMode?: boolean;
}

export function DesignScreen({ code, settings, printMode }: Props) {
  const decoded = useMemo(() => {
    try {
      return { design: decodeDesign(code), error: null as string | null };
    } catch (err) {
      return {
        design: null,
        error:
          err instanceof DesignDecodeError
            ? err.message
            : 'That link could not be read. It may have been cut short when it was copied.',
      };
    }
  }, [code]);

  const design = decoded.design;
  const guide = useMemo(
    () => (design ? buildGuide(design, settings.gauge) : null),
    [design, settings.gauge],
  );
  const id = useMemo(() => designId(code), [code]);
  const progress = useMemo(() => loadProgress(id), [id]);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (design) rememberDesign({ id, code, name: design.name });
  }, [design, id, code]);

  useEffect(() => {
    if (printMode && guide) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [printMode, guide]);

  if (!design || !guide) {
    return (
      <div className="screen">
        <section className="card">
          <h2>That design link is broken</h2>
          <p>{decoded.error}</p>
          <p className="muted small">
            Links are self-contained, so nothing was lost on a server — the address itself is
            incomplete. If it came from a message, check none of it was cut off.
          </p>
          <a className="primary as-button" href="#/">
            Design a new one
          </a>
        </section>
      </div>
    );
  }

  const repeat = design.body.style === 'normal' ? findRepeatLength(design.body) : null;
  const shareUrl = `${window.location.origin}${window.location.pathname}${hrefFor({ name: 'design', code })}`;

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      setCopied('Could not copy — select the text and copy it by hand.');
    }
  };

  return (
    <div className={printMode ? 'screen screen--print' : 'screen'}>
      <section className="card">
        <h2>{design.name}</h2>
        <p>{design.blurb}</p>
        <div className="preview-large">
          <BraceletPreview design={design} height={110} maxRows={240} />
        </div>
        <ul className="facts">
          <li>
            <strong>{guide.totalKnots.toLocaleString()}</strong> knots
          </li>
          <li>
            <strong>{guide.totalGroups}</strong> {design.body.style === 'chain' ? 'sections' : 'rows'}
          </li>
          <li>~{formatMinutes(guide.estimate.minutes)} of knotting</li>
          <li>
            {round1(guide.estimate.lengthCm)} cm long · {guide.estimate.widthMm.toFixed(0)} mm wide
          </li>
          {repeat && <li>repeats every {repeat} rows</li>}
        </ul>
      </section>

      {!printMode && (
        <section className="card card--tight actions">
          <a className="primary as-button" href={hrefFor({ name: 'knot', code })}>
            {progress && progress.done > 0
              ? `Resume knot-along (step ${progress.done + 1} of ${guide.totalKnots})`
              : 'Start knot-along'}
          </a>
          <a className="secondary as-button" href={hrefFor({ name: 'print', code })}>
            Printable guide
          </a>
          <button type="button" className="secondary" onClick={() => void copy('Link copied', shareUrl)}>
            Copy share link
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => void copy('Instructions copied', guideToText(guide))}
          >
            Copy full instructions
          </button>
          {copied && <p className="muted small">{copied}</p>}
        </section>
      )}

      {/* ---------------------------------------------------------------- */}
      <section className="card">
        <h2>Cut these lengths</h2>
        <p className="muted small">
          Cut every string to its own length below. Round numbers are cut lengths — the arithmetic
          is shown so you can check it, and so you can redo it if your knots run tighter or looser
          than the assumed gauge.
        </p>

        <table className="cuts">
          <thead>
            <tr>
              <th>String</th>
              <th>Floss</th>
              <th>Cut</th>
            </tr>
          </thead>
          <tbody>
            {guide.materials.cuts.map((cut, i) => (
              <tr key={i}>
                <td>
                  {cut.label}
                  <span className={`tag tag--${cut.confidence}`}>{cut.confidence}</span>
                </td>
                <td>
                  <span className="swatch swatch--sm" style={{ background: cut.floss.hex }} />
                  <span>
                    <strong>{cut.floss.dmc}</strong> {cut.floss.name}
                  </span>
                </td>
                <td className="cut-cell">
                  <strong>
                    {cut.pieces > 1 ? `${cut.pieces} × ` : ''}
                    {cut.cutCm} cm
                    {settings.showInches ? ` (${round1(cut.cutIn)} in)` : ''}
                  </strong>
                  <details>
                    <summary>show the arithmetic</summary>
                    <ul className="calc">
                      {cut.lines.map((line, j) => (
                        <li key={j}>
                          <span>{line.label}</span>
                          <code>{line.expression}</code>
                          <b>{fmt(line.cm)} cm</b>
                        </li>
                      ))}
                      <li className="calc-total">
                        <span>Total</span>
                        <code>
                          {cut.lines.map((l) => fmt(l.cm)).join(' + ')} = {fmt(cut.rawCm)} cm
                        </code>
                        <b>{fmt(cut.rawCm)} cm</b>
                      </li>
                      {cut.pieces > 1 ? (
                        <>
                          <li className="calc-total">
                            <span>Too long to work as one thread, so split</span>
                            <code>
                              {fmt(cut.rawCm)} ÷ {cut.pieces} + {JOIN_ALLOWANCE_CM} cm for each join
                            </code>
                            <b>{cut.cutCm} cm each</b>
                          </li>
                          <li className="calc-total">
                            <span>Total thread to buy</span>
                            <code>
                              {cut.pieces} × {cut.cutCm} = {cut.pieces * cut.cutCm} cm
                            </code>
                            <b>{fmt((cut.pieces * cut.cutCm) / 100)} m</b>
                          </li>
                        </>
                      ) : (
                        <li className="calc-total">
                          <span>Rounded up to the next 5 cm</span>
                          <code>
                            {fmt(cut.rawCm)} → {cut.cutCm}
                          </code>
                          <b>{cut.cutCm} cm</b>
                        </li>
                      )}
                    </ul>
                    <p className="muted small">{describeCut(cut)}</p>
                    {cut.note && <p className="note">{cut.note}</p>}
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3>Shopping list</h3>
        <ul className="totals">
          {guide.materials.perColorTotalCm.map((t, i) => (
            <li key={i}>
              <span className="swatch swatch--sm" style={{ background: t.floss.hex }} />
              <span>
                <strong>DMC {t.floss.dmc}</strong> {t.floss.name}
              </span>
              <b>
                {round1(t.cm / 100)} m total{t.skeins > 1 ? ` — about ${t.skeins} skeins` : ' — 1 skein is plenty'}
              </b>
            </li>
          ))}
        </ul>
        <p className="muted small">
          DMC codes come from a small built-in palette. Check the code against the real skein before
          you buy — colour names and screen colours are approximations.
        </p>

        {guide.materials.warnings.map((w, i) => (
          <p className="warn" key={i}>
            {w}
          </p>
        ))}
      </section>

      {/* ---------------------------------------------------------------- */}
      <section className="card">
        <h2>Set up</h2>
        <p className="muted small">
          Tape or clip the strings down in this order, left to right, then knot downwards.
        </p>
        <ol className="setup">
          {guide.setup.map((line) => (
            <li key={line.position}>
              <span className="pos">{line.position + 1}</span>
              <span className="swatch swatch--sm" style={{ background: line.floss.hex }} />
              <span>
                <strong>DMC {line.floss.dmc}</strong> {line.floss.name}
              </span>
              <em>{line.role}</em>
            </li>
          ))}
        </ol>
        {guide.setupNotes.map((n, i) => (
          <p key={i} className="note">
            {n}
          </p>
        ))}
        <div className="chart-wrap">
          <PatternChart design={design} maxRows={printMode ? 400 : 60} />
        </div>
        {!printMode && guide.totalGroups > 60 && (
          <p className="muted small">Chart truncated on screen. The printable guide shows all of it.</p>
        )}
      </section>

      {/* ---------------------------------------------------------------- */}
      <section className="card">
        <h2>Knots you will use</h2>
        {design.body.style === 'alpha' ? (
          <ul className="knot-legend">
            <li>
              <span className="glyph">■</span>
              <div>
                <strong>Alpha knot (double half hitch)</strong>
                <p>
                  Every knot in an alpha bracelet is the same knot: the working thread loops twice
                  around the other thread and pulls up. What changes is <em>which</em> thread does
                  the looping. Where the background shows, the long leader thread loops over the
                  base string. Where the picture shows, that base string loops over the leader
                  instead, and then drops back into its own column.
                </p>
              </div>
            </li>
          </ul>
        ) : (
          <ul className="knot-legend">
            {usedKnots(design).map((k) => (
              <li key={k}>
                <span className="glyph">{KNOT_BEHAVIOUR[k].glyph}</span>
                <div>
                  <strong>{KNOT_BEHAVIOUR[k].name}</strong>
                  <p>{KNOT_BEHAVIOUR[k].howTo}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---------------------------------------------------------------- */}
      <section className="card">
        <h2>Step by step</h2>
        <p className="muted small">
          {guide.totalKnots.toLocaleString()} steps, one per knot.
          {!printMode && ' The knot-along shows these one at a time and remembers your place.'}
        </p>
        {guide.groups.map((g) => (
          <details key={g.index} open={printMode || g.index < 2} className="group">
            <summary>
              {g.label}
              <span className="muted small">
                {' '}
                steps {g.firstStep}–{g.lastStep}
              </span>
            </summary>
            {g.note && <p className="note">{g.note}</p>}
            <ol className="steps" start={g.firstStep}>
              {guide.steps.slice(g.firstStep - 1, g.lastStep).map((s) => (
                <li key={s.n} value={s.n}>
                  <span className="swatch swatch--sm" style={{ background: design.palette[s.color]?.hex }} />
                  {s.text}
                </li>
              ))}
            </ol>
          </details>
        ))}
      </section>

      <section className="card card--tight">
        <PaletteStrip palette={design.palette} />
        <p className="muted small">
          Seed <code>{design.seed}</code>. This whole design is stored in the link itself — there is
          no account and no server.
        </p>
        {!printMode && (
          <a className="link" href="#/">
            ← Design something else
          </a>
        )}
      </section>
    </div>
  );
}

function usedKnots(design: Design): KnotType[] {
  if (design.body.style === 'normal') {
    return [...new Set(design.body.rows.flat())];
  }
  if (design.body.style === 'chain') {
    return [...new Set(design.body.modules.flatMap((m) => planModule(m).map((p) => p.knot)))];
  }
  return [];
}
